import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Brand } from './brand';
import { isSlateInventoryType } from './is-slate-inventory-type';
import { BRAND_IDENTITY } from './registry';
import type { BrandIdentity } from './registry';
import { allowsVehicleBrandUpgrade, resolveBrandFromEnvironment } from './resolve-brand';

interface BrandContextValue {
  brand: Brand;
  identity: BrandIdentity;
  /** False only while an impersonated session is still waiting for the vehicle to land. */
  isSettled: boolean;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

// Surfaces that render outside the provider tree still need a brand: the generic error
// page via RouteError, and anything mounted before BrandProvider. Cached per brand so
// consumers get a stable reference across renders.
let environmentValue: BrandContextValue | undefined;
const resolveFromEnvironment = (): BrandContextValue => {
  const brand = resolveBrandFromEnvironment();
  if (environmentValue?.brand !== brand) {
    environmentValue = { brand, identity: BRAND_IDENTITY[brand], isSettled: true };
  }
  return environmentValue;
};

interface VehicleSignals {
  inventoryType?: string | null;
  isImpersonating: boolean;
}

const BrandVehicleReportContext = createContext<(signals: VehicleSignals) => void>(() => undefined);

interface BrandProviderProps {
  brand?: Brand;
}

const BrandProvider: React.FC<React.PropsWithChildren<BrandProviderProps>> = ({
  brand,
  children
}) => {
  const [signals, setSignals] = useState<VehicleSignals>({ isImpersonating: false });

  const { inventoryType, isImpersonating } = signals;
  const base = brand ?? resolveBrandFromEnvironment();
  // Advocates reach a Slate vehicle from the Carvana domain, so the domain alone would
  // strip every Slate feature from impersonated sessions. The vehicle arrives with the
  // summary, which makes this the one brand decision that is not synchronous.
  // A pinned brand opts out: the webview entry pins Carvana and mounts no reporter, so
  // waiting on a vehicle that never lands would leave it permanently unsettled.
  const canUpgradeFromVehicle =
    isImpersonating || (brand === undefined && allowsVehicleBrandUpgrade());
  const upgradePending = base === 'carvana' && canUpgradeFromVehicle && inventoryType === undefined;
  const resolved =
    base === 'carvana' && canUpgradeFromVehicle && isSlateInventoryType(inventoryType)
      ? 'slate'
      : base;

  // Keyed on what consumers actually see, not on the reported signals: a vehicle that
  // lands without moving the brand must not hand out a new value and re-render the tree.
  const value = useMemo(
    () => ({ brand: resolved, identity: BRAND_IDENTITY[resolved], isSettled: !upgradePending }),
    [resolved, upgradePending]
  );

  return (
    <BrandContext.Provider value={value}>
      <BrandVehicleReportContext.Provider value={setSignals}>
        {children}
      </BrandVehicleReportContext.Provider>
    </BrandContext.Provider>
  );
};

const useBrand = (): BrandContextValue => useContext(BrandContext) ?? resolveFromEnvironment();

/**
 * Both signals are passed in rather than read from context so this module stays a leaf —
 * `|/brand` is imported very widely and must stay cheap.
 */
const BrandVehicleReporter: React.FC<VehicleSignals> = ({ inventoryType, isImpersonating }) => {
  const report = useContext(BrandVehicleReportContext);
  useEffect(() => {
    report({ inventoryType, isImpersonating });
  }, [report, inventoryType, isImpersonating]);
  return null;
};

export default BrandContext;
export { BrandProvider, useBrand, BrandVehicleReporter };
export type { BrandContextValue };
